import { isTemplatePlaceholder, TemplatePlaceholder } from '@/types';
import { AccordionField } from '@components/DrawerAccordion';
import { Box } from '@mui/material';
import { SearchTemplatePageContext } from '@/context/SearchTemplateContext';
import {
  BaseWidget,
  BaseWidgetProps,
  Config,
  FactoryWithContext,
} from '@react-awesome-query-builder/mui';
import { useContext } from 'react';
import TemplateCapableTextField from './templateCapableComponents/TemplateCapableTextField';

export const COMPARATOR_OPTIONS = [
  'Equal to',
  'Older than or equal to',
  'Younger than or equal to',
] as const;

export type RangeOption = (typeof COMPARATOR_OPTIONS)[number];

export type AgeCriteria = {
  range: RangeOption;
  value: number | null;
};

type AgeWidgetValue = {
  value: number | null | TemplatePlaceholder;
};

type CombinedProps = BaseWidgetProps<Config, AgeWidgetValue>;

export const AgeWidgetFactory: FactoryWithContext<CombinedProps> = ({
  value,
  setValue,
}: CombinedProps) => {
  const isOnTemplatePage = useContext(SearchTemplatePageContext);

  const handleAgeChange = (ourValue: string | TemplatePlaceholder) => {
    if (isTemplatePlaceholder(ourValue)) {
      setValue({
        value: ourValue,
      });
    } else {
      const newAgeParsed = parseInt(ourValue);
      setValue({
        value: Number.isNaN(newAgeParsed)
          ? null
          : newAgeParsed < 0
            ? newAgeParsed * -1
            : newAgeParsed,
      });
    }
  };

  const derivedValue = isTemplatePlaceholder(value?.value)
    ? value?.value
    : Number.isInteger(value?.value)
      ? value?.value?.toString()
      : ('' as unknown as string | TemplatePlaceholder);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: "row",
        gap: 3
      }}>
      <AccordionField>
        <TemplateCapableTextField
          value={derivedValue as string | TemplatePlaceholder}
          onChange={handleAgeChange}
          placeholder="Enter an age"
          label="Age"
          inputMode="numeric"
          slotProps={{ htmlInput: { pattern: '[0-9]*' } }}
          type="text"
          sx={{ width: '223px !important' }}
          isOnTemplatePage={isOnTemplatePage}
          templateBuilderDisplayName="Age filter"
        />
      </AccordionField>
    </Box>
  );
};

export default AgeWidgetFactory;

export const ageWidget: BaseWidget = {
  type: 'age',
  factory: AgeWidgetFactory,
};
